/* eslint-disable no-nested-ternary */
/* eslint-disable dot-notation */
/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
/* eslint-disable no-param-reassign */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import {
  IconChevronUp, IconCircleX, IconInfoCircle,
  IconX,
} from "@tabler/icons-react";
import React, {
  ChangeEvent, ReactNode, useEffect, useRef, useState,
} from "react";

import Badge from "../Badge/Badge";
import SearchInput from "../Input/SearchInput/SearchInput";
import Tooltip from "../Tooltip/Tooltip";
import { createChangeEvent } from "../utils/form";

import Option from "./Option/Option";

export interface SelectOption {
  label: string;
  subLabel?: string;
  value: string;
  icon?: ReactNode;
  disabled?: boolean;
  checkbox?: boolean;
}

export type SelectOptionGroups = { [optionGroup: string]: SelectOption[] }

interface SelectProps {
  label?: string;
  tooltip?: string;
  required?: boolean;
  disabled?: boolean;
  name?: string;
  placeholder?: string;
  searchPlaceHolder?: string;
  noDataLabel: string;
  options: SelectOption[] | SelectOptionGroups;
  icon?: ReactNode;
  error?: string;
}

export interface SelectSingleProps extends SelectProps {
  mode?: "single";
  allowCustomValues: false;
  value?: string;
  onChange?: (event: ChangeEvent<HTMLInputElement>, value: string) => void;
}
export interface SelectMultipleProps extends SelectProps {
  mode: "multiple" | "tags";
  allowCustomValues?: boolean;
  value?: string[];
  onChange?: (event: ChangeEvent<HTMLInputElement>, value: string[]) => void;
}

const getStyle = (active: boolean, error: boolean, disabled: boolean) => {
  if (error) return "border-border-danger-base outline-input-danger outline-offset-0";
  if (disabled) return "text-text-disabled border-border-disabled bg-bg-disabled outline-0 outline-offset-0 cursor-not-allowed";
  if (active) return "border-border-interactive-base outline-input-interactive outline-offset-0";
  return "border-border-strong hover:bg-bg-components-hover focus-within:bg-bg-components-hover focus-within:outline-focus focus-within:outline-offset-1";
};

const unifValues = (value: string | string[] | undefined): string[] => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  return [value];
};

const unifOptions = (options: SelectOption[] | SelectOptionGroups): SelectOptionGroups => {
  if (!options) return {};
  if (Array.isArray(options)) return { _____: options };
  return options;
};

interface FilterOutcome { groups: SelectOptionGroups, searchFound: boolean }

const matcher = (caseSensitive = false) => (caseSensitive
  ? (val: string, search: string) => { if (!val || !search) return false; return val.includes(search); }
  : (val: string, search: string) => { if (!val || !search) return false; return val.toLowerCase().includes(search.toLowerCase()); }
);

const filterOptionGroups = (options: SelectOptionGroups, search: string, matchFct: (a: string, b: string) => boolean): FilterOutcome => {
  const outcome = Object.entries(options).reduce((res, [grp, opts]) => {
    const [filteredOpts, found] = opts.reduce((acc, opt) => {
      if (matchFct(opt.label, search) || matchFct(opt.value, search)) return [[...acc[0], opt], opt.label === search || opt.value === search || acc[1]];
      return acc;
    }, [[], false]);

    if (filteredOpts.length > 0) return { groups: { ...res.groups, [grp]: filteredOpts }, searchFound: found || res.searchFound };
    return res;
  }, { groups: {}, searchFound: false });
  return outcome;
};

const flattenOptions = (options: SelectOptionGroups): SelectOption[] => Object.values(options).reduce((acc, cur) => [...acc, ...cur], []);

const Select = ({
  label, tooltip, required, disabled, name, placeholder, searchPlaceHolder, noDataLabel, icon, error,
  mode = "single", allowCustomValues = false, value, options, onChange,
}) => {
  const isMultiple = mode === "multiple" || mode === "tags";
  const matches = matcher(allowCustomValues);
  const [isOpen, setOpen] = useState<boolean>(false);
  const [search, setSearch] = useState<string>();
  const selectRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const searchMirrorRef = useRef<HTMLSpanElement>(null);

  const [internalValues, setInternalValues] = useState<string[]>(unifValues(value));
  const [internalOptions, setInternalOptions] = useState<SelectOptionGroups>(unifOptions(options));
  const [displayedOptions, setDisplayedOptions] = useState<SelectOptionGroups>({});

  const [highlightValue, setHighlightedValue] = useState<string>();
  const [isActive, setActive] = useState<boolean>(false);

  const toggleOpen = (v: boolean, internalAction = true) => {
    if (v) {
      if (disabled) return;
      setOpen(true);
      setActive(true);
      if (inputRef.current) {
        inputRef.current.focus();
      }
    } else {
      setOpen(false);
      setActive(false);
      if (internalAction && selectRef.current) {
        selectRef.current.focus();
      }
    }
  };

  const updateSearch = (val?: string) => {
    const trimmed = val?.trim();
    setSearch(trimmed);
    if (!inputRef.current || !searchMirrorRef.current) return;
    const txtVal = trimmed || "";
    searchMirrorRef.current.textContent = txtVal;
    inputRef.current.style.width = `${searchMirrorRef.current.clientWidth + 2}px`;
    inputRef.current.value = txtVal;
  };

  useEffect(() => {
    setInternalValues(unifValues(value));
  }, [value]);

  useEffect(() => {
    setInternalOptions(unifOptions(options));
  }, [options]);

  const resetHighlightedValue = (opts: SelectOptionGroups, newOpts: SelectOptionGroups) => {
    const fPrev = flattenOptions(opts);
    const fNext = flattenOptions(newOpts);
    if (fPrev.length !== fNext.length || fPrev.some((opt, idx) => opt.value !== fNext[idx].value)) {
      setHighlightedValue(fNext[0]?.value);
    }
  };

  useEffect(() => {
    const flatOpts = flattenOptions(internalOptions);
    const customOptions = internalValues.filter((v) => !flatOpts.some((opt) => opt.value === v))
      .map((v) => ({ label: v, value: v }));

    const { _____: ungrouped, ...others } = internalOptions;
    // eslint-disable-next-line no-underscore-dangle
    const opts = { _____: [...customOptions, ...(ungrouped || [])], ...others };
    if (!search || search === "") {
      resetHighlightedValue(displayedOptions, opts);
      setDisplayedOptions(opts);
      return;
    }
    const { groups, searchFound } = filterOptionGroups(opts, search, matches);
    const { _____: ungrouped2, ...others2 } = groups;

    const nextOpts = {
      _____: [...(!searchFound && allowCustomValues ? [{ label: search, value: search }] : []), ...(ungrouped2 || [])],
      ...others2,
    };
    resetHighlightedValue(displayedOptions, nextOpts);
    setDisplayedOptions(nextOpts);
  }, [search, internalValues, internalOptions]);

  useEffect(() => {
    if (isOpen) {
      const flatOpts = flattenOptions(displayedOptions);
      setHighlightedValue(flatOpts[0]?.value);
    } else {
      updateSearch(undefined);
    }
  }, [isOpen]);

  const onOptionSelected = (v: string) => {
    // eslint-disable-next-line no-nested-ternary
    if (onChange) {
      const flatOpts = flattenOptions(internalOptions);
      const existingOpt = flatOpts.find((opt) => opt.value === v || opt.label === v);
      if (existingOpt?.disabled) return;
      if (isMultiple) {
        const toggleVal = existingOpt?.value || v;
        const newVals = (internalValues.includes(toggleVal)
          ? internalValues.filter((val) => val !== toggleVal) : [...internalValues, v]);
        (onChange as SelectMultipleProps["onChange"])(createChangeEvent(name, newVals), newVals);
      } else {
        (onChange as SelectSingleProps["onChange"])(createChangeEvent(name, v), v);
      }
    }

    if (!isMultiple) {
      toggleOpen(false);
    }
  };

  const removeLastOption = () => {
    if (!isMultiple || internalValues.length === 0) return;
    if (onChange) {
      const newVal = internalValues.slice(0, internalValues.length - 1);
      (onChange as SelectMultipleProps["onChange"])(createChangeEvent(name, newVal), newVal);
    }
  };

  const moveHighlight = (direction: number) => {
    const flatOpts = flattenOptions(displayedOptions);

    if (!highlightValue) {
      setHighlightedValue(flatOpts[direction > 0 ? 0 : flatOpts.length - 1].value);
    } else {
      const idx = flatOpts.findIndex((opt) => opt.value === highlightValue);
      setHighlightedValue(flatOpts[(flatOpts.length + idx + direction) % flatOpts.length].value);
    }
  };

  const closeOnExternalClick = (e) => {
    if (!selectRef.current || selectRef.current.contains(e.target)) return;
    toggleOpen(false, false);
  };

  useEffect(() => {
    window.addEventListener("click", closeOnExternalClick);
    return () => {
      window.removeEventListener("click", closeOnExternalClick);
    };
  }, []);

  return (
    <div
      className="flex flex-col w-[300px] typography-medium-base gap-1"
    >
      {label && (
        <label className="flex gap-1" htmlFor={name}>
          <span>{label}</span>
          {required && <span className="text-text-danger">*</span>}
          {tooltip && (
            <Tooltip content={tooltip}>
              <span className="text-icon-bg-subtle"><IconInfoCircle /></span>
            </Tooltip>
          )}
        </label>
      )}
      <div
        className={`relative py-2.5 px-3 rounded-lg border  flex flex-nowrap gap-1 items-center 
            ${disabled ? "" : ((!isMultiple || !allowCustomValues) ? "cursor-pointer" : "cursor-text")}
            ${getStyle(isActive, error, disabled)}`}
        tabIndex={0}
        ref={(elt) => {
          if (!elt) return;
          selectRef.current = elt;
        }}
        onClick={() => {
          toggleOpen(!isOpen);
        }}
        onKeyDown={(e) => {
          switch (e.key) {
            case "Enter":
            case " ":
              if (isOpen) {
                e.stopPropagation();
                onOptionSelected(highlightValue);
                if (highlightValue === search) {
                  updateSearch(undefined);
                }
              } else {
                toggleOpen(true);
              }
              break;
            case "Backspace":
              if (!search?.length) removeLastOption();
              break;
            case "Delete":
            case "Escape":
            case "Tab":
              toggleOpen(false);
              break;
            case "ArrowUp":
              toggleOpen(true);
              moveHighlight(-1);
              break;
            case "ArrowDown":
              toggleOpen(true);
              moveHighlight(1);
              break;
            default:
            // nada
          }
        }}
      >
        {icon && !isMultiple && (
          <div className={`flex-none ${disabled ? "text-icon-disabled" : "text-icon-bg-subtle"}`}>
            {icon}
          </div>
        )}
        <div className={`grow ${isMultiple ? "flex flex-wrap gap-1" : "text-ellipsis"}`}>
          {!isMultiple && (() => {
            if (!internalValues[0]) return <span className="text-text-subtle select-none pointer-events-none">{placeholder}</span>;
            return (
              <span>
                {flattenOptions(internalOptions).find((opt) => opt.value === internalValues[0])?.label || internalValues[0]}
              </span>
            );
          })()}
          {isMultiple && (
            <>
              {!isActive && internalValues.length === 0 && (<span className="text-text-subtle select-none pointer-events-none">{placeholder}</span>)}
              {internalValues.length > 0 && ((flatOpts: SelectOption[]) => internalValues.map((v) => (
                <div key={v} className={`inline-block ${disabled ? "cursor-not-allowed" : "cursor-default"} select-none text-ellipsis whitespace-pre`}>
                  <Badge color="blue" disabled={disabled}>
                    <div className="flex gap-1 items-center">
                      {flatOpts.find((o) => o.value === v)?.label || v}
                      {!disabled && (
                        <IconX
                          className={`h-3 w-3 ${disabled ? "" : "cursor-pointer"}`}
                          onClick={(event) => {
                            if (disabled) return;
                            event.stopPropagation();
                            onOptionSelected(v); // onOptionRemoved toggles it
                          }}
                        />
                      )}
                    </div>
                  </Badge>
                </div>

              )))(flattenOptions(internalOptions))}

              {!disabled && (
                <div className="inline w-fit relative max-w-full">
                  <input
                    type="text"
                    className="appearance-none outline-hidden bg-transparent display-none"
                    // style={{ width: `${(search?.length || 0) + 1}ch` }}
                    onChange={(e) => { updateSearch(e.target.value); }}
                    ref={(elt) => {
                      if (elt) {
                        inputRef.current = elt;
                      }
                    }}
                  />
                  {/* search mirror element to determine length of input */}
                  <span
                    className="absolute opacity-0 left-0 top-0 select-none"
                    aria-hidden="true"
                    ref={(elt) => {
                      if (elt) {
                        searchMirrorRef.current = elt;
                      }
                    }}
                  />
                </div>
              )}

            </>
          )}
        </div>
        <div className={`${disabled ? "text-icon-disabled" : "text-icon-bg-subtle"}`}>
          <IconChevronUp className={`transition-transform ease-in-out ${isOpen ? "rotate-180" : ""}`} />
        </div>
        {/* OPTION LIST */}
        {isOpen && (
          <div className="absolute top-full max-h-96 pt-2 left-1/2 -translate-x-1/2 mx-auto min-w-full w-fit stop-pr z-10" onClick={(e) => e.stopPropagation()}>
            <div className="bg-white">
              <div className="py-3 border border-border-strong rounded-lg shadow-md flex flex-col max-h-96">
                {!isMultiple && (
                  <div className="p-3 flex-none">
                    <SearchInput
                      value={search}
                      onChange={(e) => updateSearch(e.target.value)}
                      placeholder={searchPlaceHolder || ""}
                    />
                  </div>
                )}
                {Object.values(displayedOptions).some((opts) => opts.length > 0) ? (
                  <div className="flex flex-col grow overflow-y-auto text-text-base text-nowrap">
                    {
                      Object.entries(displayedOptions).map(([group, opts]) => (
                        <React.Fragment key={group}>
                          {opts?.length > 0 && group !== "_____" && (<div className="p-3 typography-small-strong">{group}</div>)}
                          {opts.map((opt) => (
                            <Option
                              {...opt}
                              selected={internalValues.includes(opt.value)}
                              highlighted={highlightValue === opt.value}
                              onClick={(v, _) => onOptionSelected(v)}
                            />
                          ))}
                        </React.Fragment>
                      ))
                    }
                  </div>
                ) : (
                  <div className="p-3 text-text-subtle flex flex-col gap-2 py-5 px-2 items-center">
                    <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <g clipPath="url(#clip0_1657_3839)">
                        <path d="M50.7873 17.9917H29.2308C28.7395 17.9924 28.2686 18.1925 27.9212 18.5484C27.5739 18.9042 27.3785 19.3867 27.3779 19.89V68.9714L27.1308 69.0486L21.8427 70.7076C21.592 70.7859 21.3213 70.7591 21.09 70.6331C20.8586 70.5072 20.6855 70.2923 20.6087 70.0357L4.87881 17.3982C4.80226 17.1415 4.82835 16.8641 4.95135 16.627C5.07435 16.39 5.28418 16.2127 5.53474 16.134L13.6838 13.5778L37.3082 6.16968L45.4571 3.61342C45.5812 3.57432 45.7115 3.56065 45.8407 3.57321C45.9698 3.58576 46.0953 3.62429 46.2099 3.68659C46.3246 3.74889 46.4261 3.83373 46.5087 3.93626C46.5912 4.03879 46.6533 4.157 46.6912 4.28412L50.7119 17.7386L50.7873 17.9917Z" fill="#F8FAFC" />
                        <path d="M55.4912 17.7386L50.6452 1.52285C50.5646 1.25267 50.4328 1.0014 50.2574 0.783422C50.0819 0.565441 49.8663 0.385014 49.6227 0.25245C49.3792 0.119886 49.1125 0.0377827 48.838 0.0108346C48.5635 -0.0161135 48.2865 0.0126217 48.0228 0.0953953L36.5657 3.68808L12.9426 11.0974L1.48559 14.6914C0.953361 14.8588 0.507714 15.2357 0.246473 15.7394C-0.0147672 16.2431 -0.0702583 16.8324 0.0921825 17.378L16.6546 72.7969C16.7865 73.2373 17.0528 73.6228 17.4143 73.8968C17.7758 74.1708 18.2135 74.3188 18.6631 74.3193C18.8713 74.3194 19.0782 74.2874 19.277 74.2244L27.1308 71.7618L27.3779 71.6833V71.4188L27.1308 71.496L19.2042 73.9827C18.7344 74.1294 18.227 74.0792 17.7932 73.8432C17.3595 73.6071 17.0349 73.2045 16.8905 72.7235L0.329403 17.3033C0.257893 17.065 0.23297 16.8146 0.25606 16.5664C0.27915 16.3183 0.349798 16.0772 0.463963 15.857C0.578129 15.6368 0.733569 15.4419 0.921377 15.2834C1.10919 15.1248 1.32568 15.0058 1.55845 14.9331L13.0155 11.3391L36.6387 3.93105L48.0957 0.337103C48.2723 0.281903 48.4558 0.253758 48.6404 0.253582C49.0366 0.254493 49.4221 0.385337 49.7404 0.62695C50.0587 0.868563 50.2931 1.20825 50.4094 1.59625L55.233 17.7386L55.3096 17.9917H55.5666L55.4912 17.7386Z" fill="#CBD5E1" />
                        <path d="M15.1543 16.1754C14.9162 16.1752 14.6845 16.0969 14.493 15.9519C14.3016 15.8069 14.1606 15.6029 14.0906 15.3697L12.4995 10.0456C12.4568 9.90262 12.4419 9.75238 12.4559 9.60349C12.4698 9.4546 12.5122 9.30999 12.5807 9.1779C12.6492 9.04581 12.7424 8.92884 12.8551 8.83366C12.9677 8.73849 13.0975 8.66698 13.2371 8.62321L34.9702 1.80689C35.2521 1.71875 35.5567 1.74881 35.817 1.89047C36.0773 2.03212 36.2722 2.2738 36.3587 2.56246L37.9498 7.8866C38.0358 8.17543 38.0064 8.48741 37.8681 8.75408C37.7299 9.02076 37.494 9.22035 37.2123 9.30909L15.4791 16.1254C15.3739 16.1585 15.2644 16.1753 15.1543 16.1754Z" fill="#CBD5E1" />
                        <path d="M23.489 5.6889C24.8534 5.6889 25.9595 4.55575 25.9595 3.15795C25.9595 1.76014 24.8534 0.626999 23.489 0.626999C22.1245 0.626999 21.0184 1.76014 21.0184 3.15795C21.0184 4.55575 22.1245 5.6889 23.489 5.6889Z" fill="#CBD5E1" />
                        <path d="M23.489 4.76063C24.353 4.76063 25.0534 4.04308 25.0534 3.15795C25.0534 2.27282 24.353 1.55527 23.489 1.55527C22.625 1.55527 21.9246 2.27282 21.9246 3.15795C21.9246 4.04308 22.625 4.76063 23.489 4.76063Z" fill="white" />
                        <path d="M74.4413 73.6726H32.6895C32.4111 73.6723 32.1442 73.5589 31.9474 73.3572C31.7506 73.1556 31.6398 72.8822 31.6395 72.597V21.3452C31.6398 21.0601 31.7506 20.7867 31.9474 20.585C32.1442 20.3833 32.4111 20.2699 32.6895 20.2696H74.4413C74.7197 20.2699 74.9866 20.3834 75.1834 20.585C75.3803 20.7867 75.491 21.0601 75.4913 21.3452V72.597C75.491 72.8821 75.3802 73.1555 75.1834 73.3572C74.9866 73.5588 74.7197 73.6723 74.4413 73.6726Z" fill="#F8FAFC" />
                        <path d="M55.2331 17.7386H29.2308C28.6741 17.7395 28.1404 17.9664 27.7468 18.3696C27.3531 18.7729 27.1316 19.3196 27.1309 19.89V71.496L27.3779 71.4188V19.89C27.3785 19.3867 27.5739 18.9042 27.9213 18.5484C28.2686 18.1925 28.7396 17.9924 29.2308 17.9917H55.3097L55.2331 17.7386ZM77.9001 17.7386H29.2308C28.6741 17.7395 28.1404 17.9664 27.7468 18.3696C27.3531 18.7729 27.1316 19.3196 27.1309 19.89V77.8487C27.1316 78.419 27.3531 78.9657 27.7468 79.369C28.1404 79.7723 28.6741 79.9992 29.2308 80H77.9001C78.4568 79.9992 78.9905 79.7723 79.3841 79.369C79.7778 78.9657 79.9993 78.419 80 77.8487V19.89C79.9993 19.3196 79.7778 18.7729 79.3841 18.3696C78.9905 17.9664 78.4568 17.7395 77.9001 17.7386ZM79.753 77.8487C79.7524 78.3519 79.557 78.8344 79.2096 79.1903C78.8623 79.5461 78.3913 79.7463 77.9001 79.7469H29.2308C28.7396 79.7463 28.2686 79.5461 27.9213 79.1903C27.5739 78.8344 27.3785 78.3519 27.3779 77.8487V19.89C27.3785 19.3867 27.5739 18.9042 27.9213 18.5484C28.2686 18.1925 28.7396 17.9924 29.2308 17.9917H77.9001C78.3913 17.9924 78.8623 18.1925 79.2096 18.5484C79.557 18.9042 79.7524 19.3867 79.753 19.89V77.8487Z" fill="#CBD5E1" />
                        <path d="M64.9299 23.3067H42.2011C41.9063 23.3064 41.6238 23.1863 41.4153 22.9728C41.2069 22.7593 41.0897 22.4698 41.0894 22.1678V16.5997C41.0897 16.2978 41.2069 16.0083 41.4153 15.7947C41.6238 15.5812 41.9063 15.4611 42.2011 15.4608H64.9299C65.2246 15.4611 65.5072 15.5812 65.7156 15.7947C65.9241 16.0083 66.0413 16.2978 66.0416 16.5997V22.1678C66.0413 22.4698 65.9241 22.7593 65.7157 22.9728C65.5072 23.1863 65.2246 23.3064 64.9299 23.3067Z" fill="#CBD5E1" />
                        <path d="M53.5654 15.8404C54.9298 15.8404 56.0359 14.7073 56.0359 13.3095C56.0359 11.9117 54.9298 10.7785 53.5654 10.7785C52.2009 10.7785 51.0948 11.9117 51.0948 13.3095C51.0948 14.7073 52.2009 15.8404 53.5654 15.8404Z" fill="#CBD5E1" />
                        <path d="M53.5655 14.8511C54.3965 14.8511 55.0702 14.1609 55.0702 13.3095C55.0702 12.4581 54.3965 11.7679 53.5655 11.7679C52.7344 11.7679 52.0607 12.4581 52.0607 13.3095C52.0607 14.1609 52.7344 14.8511 53.5655 14.8511Z" fill="white" />
                      </g>
                      <defs>
                        <clipPath id="clip0_1657_3839">
                          <rect width="80" height="80" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>

                    <div>{noDataLabel}</div>
                  </div>
                )}
              </div>
            </div>

          </div>
        )}
      </div>

      {error && (
        <div className="flex gap-1">
          <IconCircleX className="text-icon-danger" />
          <span className="text-text-danger">{error}</span>
        </div>
      )}
    </div>
  );
};

export default Select;
